import { reactive, onUnmounted } from 'vue';

const HEADER_HEIGHT = 40; // h-10 = 40px
const SNAP_MINUTES = 15;
const MIN_DRAG_DISTANCE = 4; // px — evita drag acidental em cliques simples

export function useMeetingDrag({ visibleDays, columnRefs, emit }) {
  const dragState = reactive({
    draggingId: null,
  });

  let activeDrag = null;
  let clone = null;

  function snapMinutes(raw) {
    const snapped = Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES;
    return Math.max(0, Math.min(snapped, 23 * 60 + 45));
  }

  function minutesToTime(minutes) {
    const h = String(Math.floor(minutes / 60)).padStart(2, '0');
    const m = String(minutes % 60).padStart(2, '0');
    return `${h}:${m}`;
  }

  function getTargetDay(clientX) {
    const days = visibleDays.value;
    const refs = columnRefs.value;
    for (let i = 0; i < days.length; i += 1) {
      const el = refs[i];
      if (el) {
        const rect = el.getBoundingClientRect();
        if (clientX >= rect.left && clientX <= rect.right) {
          return days[i];
        }
      }
    }
    return null;
  }

  function getGridTop(columnIndex) {
    const el = columnRefs.value[columnIndex];
    if (!el) return null;
    return el.getBoundingClientRect().top + HEADER_HEIGHT;
  }

  function createClone(meeting, event, cardEl) {
    const rect = cardEl.getBoundingClientRect();

    const el = document.createElement('div');
    el.className = [
      'fixed',
      'z-[9999]',
      'pointer-events-none',
      'rounded',
      'px-1',
      'py-0.5',
      'text-xs',
      'shadow-lg',
      'opacity-90',
      'text-white',
    ].join(' ');
    el.style.backgroundColor = meeting.user?.color ?? '';
    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
    el.style.left = `${event.clientX - activeDrag.offsetX}px`;
    el.style.top = `${event.clientY - activeDrag.offsetY}px`;

    const titleEl = document.createElement('p');
    titleEl.className = 'font-medium truncate leading-tight';
    titleEl.textContent = meeting.title;

    const timeEl = document.createElement('p');
    timeEl.className = 'truncate opacity-80';
    timeEl.textContent = minutesToTime(activeDrag.snappedMinutes);

    el.appendChild(titleEl);
    el.appendChild(timeEl);
    document.body.appendChild(el);

    activeDrag.cloneTimeEl = timeEl;

    return el;
  }

  function onMousemove(event) {
    if (!activeDrag) return;

    const { startX, startY } = activeDrag;
    const moved =
      Math.abs(event.clientX - startX) > MIN_DRAG_DISTANCE ||
      Math.abs(event.clientY - startY) > MIN_DRAG_DISTANCE;

    if (!moved && !activeDrag.started) return;

    if (!activeDrag.started) {
      activeDrag.started = true;
      dragState.draggingId = activeDrag.meeting.id;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      clone = createClone(activeDrag.meeting, event, activeDrag.cardEl);
    }

    clone.style.left = `${event.clientX - activeDrag.offsetX}px`;
    clone.style.top = `${event.clientY - activeDrag.offsetY}px`;

    const targetDay = getTargetDay(event.clientX);
    if (targetDay) {
      const dayIndex = visibleDays.value.findIndex(
        d => d.toDateString() === targetDay.toDateString()
      );
      const gridTop = getGridTop(dayIndex);
      if (gridTop === null) return;
      const rawMinutes =
        event.clientY - gridTop - activeDrag.offsetY + HEADER_HEIGHT;
      activeDrag.snappedMinutes = snapMinutes(rawMinutes);
      activeDrag.targetDay = targetDay;

      if (activeDrag.cloneTimeEl) {
        activeDrag.cloneTimeEl.textContent = minutesToTime(
          activeDrag.snappedMinutes
        );
      }
    }
  }

  function onMouseup() {
    if (!activeDrag) return;

    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    if (clone) {
      clone.remove();
      clone = null;
    }

    dragState.draggingId = null;

    if (!activeDrag.started) {
      activeDrag = null;
      return;
    }

    const { meeting, targetDay, snappedMinutes } = activeDrag;
    activeDrag = null;

    if (!targetDay) return;

    const originalStart = new Date(meeting.start_at);
    const originalEnd = new Date(meeting.end_at);
    const duration = originalEnd - originalStart;

    const newStart = new Date(targetDay);
    newStart.setHours(0, snappedMinutes, 0, 0);
    const newEnd = new Date(newStart.getTime() + duration);

    if (
      newStart.getTime() === originalStart.getTime() &&
      newEnd.getTime() === originalEnd.getTime()
    ) {
      return;
    }

    emit('meetingDrop', {
      id: meeting.id,
      start_at: newStart.toISOString(),
      end_at: newEnd.toISOString(),
      originalMeeting: meeting,
    });
  }

  function onMeetingMousedown(event, meeting, cardEl) {
    if (activeDrag) {
      document.removeEventListener('mousemove', onMousemove);
      document.removeEventListener('mouseup', onMouseup);
      if (clone) {
        clone.remove();
        clone = null;
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      dragState.draggingId = null;
      activeDrag = null;
    }

    event.preventDefault();

    const rect = cardEl.getBoundingClientRect();
    activeDrag = {
      meeting,
      cardEl,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      started: false,
      targetDay: null,
      snappedMinutes: 0,
      cloneTimeEl: null,
    };

    document.addEventListener('mousemove', onMousemove);
    document.addEventListener('mouseup', onMouseup);
  }

  onUnmounted(() => {
    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    if (clone) {
      clone.remove();
      clone = null;
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    activeDrag = null;
  });

  return { onMeetingMousedown, dragState };
}
